import React from 'react';
import { ArrowRight, X } from 'lucide-react';
import { IconButton } from '../../../../components/ui';
import { govDate } from '../../format';
import {
  ActEffects,
  ENDING_LABEL,
  TimelineLevel,
  goneSentence,
  lengthOf,
  personOf,
  personTone,
} from '../../timeline';
import type { GovActView, SeatView, TenureView } from '../../types';
import { ActCard, actName, postLabel } from './TimelineActs';
import type { TimelineSelection } from './TimelineChart';

/** What was chosen on the chart: one tenure, or one act */
export const TimelineDetail: React.FC<{
  selected: NonNullable<TimelineSelection>;
  acts: GovActView[];
  tenures: TenureView[];
  effects: Map<string, ActEffects>;
  seatOf: Map<number, SeatView>;
  level: TimelineLevel;
  onSelect: (next: TimelineSelection) => void;
  /** Shown in a narrow column: the dates stack */
  column?: boolean;
}> = ({ selected, acts, tenures, effects, seatOf, level, onSelect, column = false }) => {
  const close = (
    <IconButton label="Close" size="sm" onClick={() => onSelect(null)}>
      <X className="h-4 w-4" strokeWidth={1.75} />
    </IconButton>
  );

  if (selected.kind === 'act') {
    const act = acts.find((item) => item.id === selected.id);
    if (!act) return null;
    return (
      <div className="relative rounded-2xl border border-neutral-200 bg-white">
        <div className="absolute right-2 top-2 z-10">{close}</div>
        <div className="pr-10">
          <ActCard
            bare
            act={act}
            effects={effects.get(act.id)}
            tenures={tenures}
            seatOf={seatOf}
            level={level}
            onSelectTenure={(index) => onSelect({ kind: 'tenure', index })}
          />
        </div>
      </div>
    );
  }

  const tenure = tenures[selected.index];
  if (!tenure) return null;
  const tone = personTone(tenure);
  const seat = seatOf.get(selected.index);
  const others = tenures
    .map((item, index) => ({ item, index }))
    .filter(({ item, index }) => index !== selected.index && personOf(item) === personOf(tenure))
    .sort((a, b) => (a.item.from < b.item.from ? 1 : -1));
  const actLink = (act: { id: string; date: string; reference: string | null }) => (
    <button
      type="button"
      onClick={() => onSelect({ kind: 'act', id: act.id })}
      className="text-left text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
    >
      {actName(act)}
    </button>
  );

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white">
      <div className="flex items-start gap-3 px-4 pt-3.5">
        <span
          className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[13px]"
          style={{ backgroundColor: tone.fill, color: tone.ink, boxShadow: `inset 0 0 0 1.5px ${tone.edge}` }}
        >
          {tenure.person
            .split(/\s+/)
            .filter((word) => /^\p{Lu}/u.test(word))
            .slice(-2)
            .map((word) => word[0])
            .join('')}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] leading-tight text-ink">{tenure.person}</p>
          <p className="mt-0.5 break-words text-[12.5px] leading-snug text-neutral-500">{tenure.title}</p>
          {seat && seat.label !== tenure.title && (
            <p className="text-[11.5px] text-neutral-400">
              {postLabel(seat.post)} · {seat.label}
            </p>
          )}
          {(seat?.gone ?? seat?.ended) && (
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] text-neutral-500">
              <span className="rounded-full bg-neutral-200/70 px-1.5 py-px text-[9.5px] uppercase tracking-[0.05em] text-neutral-500">
                Defunct
              </span>
              {goneSentence((seat.gone ?? seat.ended)!)}
            </p>
          )}
        </div>
        {close}
      </div>

      <dl
        className={`mt-3 grid grid-cols-1 border-t border-neutral-100 ${
          column ? 'divide-y divide-neutral-100' : 'sm:grid-cols-3 sm:divide-x sm:divide-neutral-100'
        }`}
      >
        <div className="px-4 py-3">
          <dt className="text-[11px] uppercase tracking-[0.06em] text-neutral-400">
            {tenure.openStart ? 'In post by' : 'Took the post'}
          </dt>
          <dd className="mt-0.5 text-[13px] tabular-nums text-ink">{govDate(tenure.from)}</dd>
          <dd className="mt-0.5 text-[11.5px]">{actLink(tenure.fromAct)}</dd>
          {tenure.openStart && (
            <dd className="mt-1 text-[11.5px] leading-snug text-neutral-400">
              The first act on file already lists them; when they took the post is looked for further back.
            </dd>
          )}
        </div>
        <div className="px-4 py-3">
          <dt className="text-[11px] uppercase tracking-[0.06em] text-neutral-400">{tenure.to ? 'Left' : 'Now'}</dt>
          <dd className="mt-0.5 text-[13px] tabular-nums text-ink">
            {tenure.to ? govDate(tenure.to) : tenure.acting ? 'Acting' : 'In post'}
          </dd>
          {tenure.toAct && <dd className="mt-0.5 text-[11.5px]">{actLink(tenure.toAct)}</dd>}
          {tenure.ended && (
            <dd className="mt-0.5 text-[11.5px] text-neutral-400">{ENDING_LABEL[tenure.ended]}</dd>
          )}
        </div>
        <div className="px-4 py-3">
          <dt className="text-[11px] uppercase tracking-[0.06em] text-neutral-400">Length</dt>
          <dd className="mt-0.5 text-[13px] tabular-nums text-ink">{lengthOf(tenure.from, tenure.to)}</dd>
          {tenure.confirmedBy.length > 0 && (
            <dd className="mt-0.5 text-[11.5px] text-neutral-400">
              Kept by{' '}
              {tenure.confirmedBy.map((act, index) => (
                <React.Fragment key={act.id}>
                  {index > 0 && ', '}
                  {actLink(act)}
                </React.Fragment>
              ))}
            </dd>
          )}
        </div>
      </dl>

      {others.length > 0 && (
        <div className="border-t border-neutral-100 px-4 py-3">
          <p className="mb-1.5 text-[11px] uppercase tracking-[0.06em] text-neutral-400">Other posts</p>
          <ul className="space-y-0.5">
            {others.map(({ item, index }) => (
              <li key={index}>
                <button
                  type="button"
                  onClick={() => onSelect({ kind: 'tenure', index })}
                  className="flex w-full min-w-0 items-center gap-2 rounded-lg px-1.5 py-1 text-left text-[12.5px] transition-colors hover:bg-neutral-50"
                >
                  <span className="min-w-0 flex-1 truncate text-ink">{item.title}</span>
                  <span className="flex-shrink-0 tabular-nums text-neutral-400">
                    {govDate(item.from)} – {item.to ? govDate(item.to) : 'today'}
                  </span>
                  <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
